import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ClipboardDocumentIcon, CpuChipIcon, ExclamationTriangleIcon, InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { getApiErrorMessage } from '../../constants/messages';
import { McpCapability, McpStatus, McpToken, McpTokenIssued, Project } from '../../types/api';

interface Props {
  organisationId: string;
  isAdmin: boolean;
}

const CAPACITES: McpCapability[] = ['read', 'write', 'operate'];

// Une carte pour le serveur MCP : l'adresse a coller, les jetons vivants, et l'etat.
//
// Deux choses que l'ecran doit DIRE, faute de quoi elles se decouvrent a l'usage :
// un jeton ne vaut que pour UNE organisation, et le serveur ne supprime rien. La
// seconde evite qu'on cherche longtemps un outil qui n'existe pas.
const OrganisationMcpSettings: React.FC<Props> = ({ organisationId, isAdmin }) => {
  const { t } = (useTranslation as any)('organisations');

  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [status, setStatus] = useState<McpStatus | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Le jeton n'est rendu qu'a la creation : une fois cet etat vide, il est perdu.
  const [issued, setIssued] = useState<McpTokenIssued | null>(null);
  const [form, setForm] = useState<{ label: string; capabilities: McpCapability[]; project_id: string }>({
    label: '', capabilities: ['read'], project_id: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const liste = await apiService.listMcpTokens(organisationId);
      setTokens(liste);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('settings.mcp.loadError')));
    }
    if (isAdmin) {
      // L'etat est reserve aux administrateurs : un refus n'est pas une erreur, la
      // carte reste utilisable sans lui.
      try {
        setStatus(await apiService.getMcpStatus(organisationId));
      } catch {
        setStatus(null);
      }
    }
    try {
      setProjects(await apiService.getProjects());
    } catch {
      setProjects([]);
    }
    setLoading(false);
  }, [organisationId, isAdmin, t]);

  useEffect(() => { load(); }, [load]);

  /** La commande de branchement. Avec le jeton quand il vient d'etre emis -- c'est le
   *  seul instant ou il est visible -- avec un substitut sinon. */
  const commande = (adresse: string, jeton?: string) =>
    `claude mcp add --transport http nubiecloud ${adresse} \\\n  --header "Authorization: Bearer ${jeton ?? 'VOTRE_JETON'}"`;

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('settings.mcp.copied'));
    } catch {
      toast.error(t('settings.mcp.copyError'));
    }
  };

  const basculerCapacite = (c: McpCapability) => {
    setForm((f) => {
      const presente = f.capabilities.includes(c);
      // Au moins une capacite, sinon le jeton ne verrait aucun outil et l'utilisateur
      // conclurait a une panne.
      const suivantes = presente ? f.capabilities.filter((x) => x !== c) : [...f.capabilities, c];
      return { ...f, capabilities: suivantes.length ? suivantes : ['read'] };
    });
  };

  const emettre = async () => {
    setBusy(true);
    try {
      const r = await apiService.issueMcpToken(organisationId, {
        label: form.label.trim() || undefined,
        capabilities: form.capabilities,
        project_id: form.project_id || undefined,
      });
      setIssued(r);
      setShowForm(false);
      setForm({ label: '', capabilities: ['read'], project_id: '' });
      toast.success(t('settings.mcp.issued'));
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('settings.mcp.issueError')));
    } finally {
      setBusy(false);
    }
  };

  const revoquer = async (jeton: McpToken) => {
    if (!window.confirm(t('settings.mcp.confirmRevoke', { prefix: jeton.token_prefix }) as string)) return;
    setBusy(true);
    try {
      await apiService.revokeMcpToken(organisationId, jeton.id);
      toast.success(t('settings.mcp.revoked'));
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('settings.mcp.revokeError')));
    } finally {
      setBusy(false);
    }
  };

  const url = status?.mcp_url ?? issued?.mcp_url ?? '';

  return (
    <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-medium text-text-primary flex items-center">
            <CpuChipIcon className="h-5 w-5 mr-2 text-text-quaternary" />
            {t('settings.mcp.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('settings.mcp.description')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center px-3 py-2 text-sm rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50"
          disabled={busy}
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          {t('settings.mcp.new')}
        </button>
      </div>

      {url && (
        <div className="mt-4 p-3 rounded-md bg-surface-3 text-xs text-text-tertiary">
          <p className="font-medium text-text-secondary mb-1">{t('settings.mcp.endpointLabel')}</p>
          <div className="flex items-center space-x-2">
            <code className="text-text-primary break-all">{url}</code>
            <button type="button" onClick={() => copy(url)} className="text-accent-400 hover:text-accent-300" title={t('settings.mcp.copy') as string}>
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2">{t('settings.mcp.endpointHint')}</p>

          <p className="font-medium text-text-secondary mt-3 mb-1">{t('settings.mcp.connectLabel')}</p>
          <div className="flex items-start space-x-2">
            <code className="text-text-primary break-all whitespace-pre-wrap flex-1">{commande(url)}</code>
            <button type="button" onClick={() => copy(commande(url))} className="text-accent-400 hover:text-accent-300 shrink-0" title={t('settings.mcp.copy') as string}>
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2">{t('settings.mcp.connectHint')}</p>

          {/* La promesse « collez et ca marche » n'est pas encore tenable : le serveur
              suit la revision 2026-07-28, les clients actuels parlent 2025-06-18. Le
              taire ferait conclure au premier essayeur que le serveur est casse.
              Ton SOBRE a dessein : c'est une information de version, pas une alerte. */}
          <div className="mt-3 flex items-start space-x-2 rounded-md border border-border-2 bg-surface-2 p-2">
            <InformationCircleIcon className="h-4 w-4 shrink-0 mt-px text-text-tertiary" />
            <p className="text-text-secondary">{t('settings.mcp.compatWarning')}</p>
          </div>
          <p className="mt-2 text-text-secondary">{t('settings.mcp.noDestroyHint')}</p>
        </div>
      )}

      {issued && (
        <div className="mt-4 p-4 rounded-md bg-accent-soft border border-accent/30">
          <p className="text-sm font-medium text-accent-emphasis flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-accent-emphasis" />
            {t('settings.mcp.shownOnce')}
          </p>
          <div className="mt-2 flex items-center space-x-2">
            <code className="text-xs text-text-primary break-all bg-surface-3 px-2 py-1 rounded">{issued.token}</code>
            <button type="button" onClick={() => copy(issued.token)} className="text-accent-400 hover:text-accent-300">
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">{t('settings.mcp.oneOrgHint')}</p>

          {url && (
            <div className="mt-3">
              <p className="text-xs font-medium text-accent-emphasis mb-1">{t('settings.mcp.connectReady')}</p>
              <div className="flex items-start space-x-2">
                <code className="text-xs text-text-primary break-all whitespace-pre-wrap bg-surface-3 px-2 py-1 rounded flex-1">{commande(url, issued.token)}</code>
                <button type="button" onClick={() => copy(commande(url, issued.token))} className="text-accent-400 hover:text-accent-300 shrink-0" title={t('settings.mcp.copy') as string}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <button type="button" onClick={() => setIssued(null)} className="mt-3 text-xs text-text-tertiary hover:text-text-secondary underline">
            {t('settings.mcp.hideToken')}
          </button>
        </div>
      )}

      {showForm && (
        <div className="mt-4 p-4 rounded-md bg-surface-3 space-y-3">
          <div>
            <label className="block text-xs text-text-tertiary mb-1">{t('settings.mcp.labelField')}</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t('settings.mcp.labelPlaceholder') as string}
              className="w-full px-3 py-2 text-sm rounded-md bg-surface-2 border border-border-1 text-text-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-tertiary mb-1">{t('settings.mcp.capabilities')}</label>
            <div className="flex flex-wrap gap-2">
              {CAPACITES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => basculerCapacite(c)}
                  className={`px-3 py-1 text-xs rounded-full border ${
                    form.capabilities.includes(c)
                      ? 'bg-accent-500/15 border-accent-500 text-accent-300'
                      : 'bg-surface-2 border-border-1 text-text-tertiary'
                  }`}
                >
                  {t(`settings.mcp.cap.${c}`)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-text-tertiary">{t('settings.mcp.capHint')}</p>
          </div>
          <div>
            <label className="block text-xs text-text-tertiary mb-1">{t('settings.mcp.scope')}</label>
            <select
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full px-3 py-2 text-sm rounded-md bg-surface-2 border border-border-1 text-text-primary"
            >
              <option value="">{t('settings.mcp.scopeAll')}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-2 text-sm text-text-tertiary hover:text-text-secondary">
              {t('cancel', { ns: 'common' })}
            </button>
            <button type="button" onClick={emettre} disabled={busy} className="px-3 py-2 text-sm rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50">
              {t('settings.mcp.issue')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 divide-y divide-border-subtle">
        {loading && <p className="p-6 text-center text-sm text-text-tertiary">{t('loading', { ns: 'common' })}</p>}
        {!loading && tokens.length === 0 && (
          <p className="p-6 text-center text-sm text-text-tertiary">{t('settings.mcp.empty')}</p>
        )}
        {!loading && tokens.map((jeton) => (
          <div key={jeton.id} className="py-3 flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm text-text-primary truncate">
                {jeton.label || t('settings.mcp.noLabel')}
                <code className="ml-2 text-xs text-text-tertiary">nbc_mcp_{jeton.token_prefix}…</code>
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">
                {jeton.capabilities.map((c) => t(`settings.mcp.cap.${c}`)).join(' · ')}
                {jeton.project_id && ` · ${t('settings.mcp.scopedToProject')}`}
                {jeton.last_seen_at
                  ? ` · ${t('settings.mcp.lastSeen', { date: new Date(jeton.last_seen_at).toLocaleString() })}`
                  : ` · ${t('settings.mcp.neverUsed')}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => revoquer(jeton)}
              disabled={busy}
              className="text-text-tertiary hover:text-red-400 disabled:opacity-50"
              title={t('settings.mcp.revoke') as string}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {status && (
        <div className="mt-4 p-3 rounded-md bg-surface-3 text-xs text-text-tertiary">
          <p className="font-medium text-text-secondary mb-1">{t('settings.mcp.status')}</p>
          <p>
            {t('settings.mcp.statusLine', {
              total: status.last_24h.total,
              errors: status.last_24h.errors,
              tools: status.outils_disponibles.length,
            })}
          </p>
          {status.recent_events.length > 0 && (
            <ul className="mt-2 space-y-1">
              {status.recent_events.slice(0, 5).map((e) => (
                <li key={e.id} className="flex items-center justify-between">
                  <span className="truncate">{e.method}{e.tool_name ? ` · ${e.tool_name}` : ''}</span>
                  <span className={e.status_code >= 400 ? 'text-red-400' : 'text-text-quaternary'}>{e.status_code}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganisationMcpSettings;
